/* [PATCH oss-temelji v1] OSS (unijska ureditev VEM) — drzave in konti.
   NAVODILA_oss_korak_1_temelji_v1_20260903 §3, po vzoru Opalove maske
   "Vnos drzav VEM 6.7.K".

   Vrstice stopenj NISO prosto polje: pridejo iz globalnega sifranta
   oss_stopnje na danasnji dan (za AT: 20 / 13 / 10). Racunovodkinja vpise
   samo konte. Konti se validirajo na backendu proti kontnemu planu; napako
   izpisemo DOBESEDNO, brez preoblikovanja. */
import React, { useCallback, useEffect, useState } from 'react';
import { Globe, Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { KontoInput } from '../KontoInput';

interface Stopnja { vrsta_stopnje: string; stopnja: number; }
interface Drzava { iso2: string; iso3: string; num: string; naziv_sl: string; }
interface Vrstica {
  drzava: string;
  naziv_sl?: string;
  num?: string;
  konto_prihodki_blago?: string | null;
  konto_prihodki_storitve?: string | null;
  aktivna: boolean;
  stopnje: Array<{ stopnja: number; konto_ddv: string }>;
}

/* [PATCH oss-temelji v1] korak 1b: pet rez. Vrstni red prikaza doloca
   oss_util.stopnje_za() (splosna -> nizja -> posebna_nizja -> super_nizja
   -> parkirna), zato ga tu ne razvrscamo znova. */
const OZNAKA_STOPNJE: Record<string, string> = {
  splosna: 'splošna', nizja: 'nižja', posebna_nizja: 'posebna nižja',
  super_nizja: 'super nižja', parkirna: 'parkirna',
};

export function OssDrzaveKartica({ companyId }: { companyId: string }) {
  const [vkljucen, setVkljucen] = useState(false);
  const [od, setOd] = useState('');
  const [vrstice, setVrstice] = useState<Vrstica[]>([]);
  const [sifrant, setSifrant] = useState<Drzava[]>([]);
  const [stopnjeZa, setStopnjeZa] = useState<Record<string, Stopnja[]>>({});
  const [nova, setNova] = useState('');
  const [napaka, setNapaka] = useState('');
  const [sporocilo, setSporocilo] = useState('');
  const [nalagam, setNalagam] = useState(true);

  const naloziStopnje = useCallback((iso2: string) => {
    if (!iso2) return;
    setStopnjeZa(prej => (prej[iso2] ? prej : { ...prej, [iso2]: [] }));
    api.get(`/oss/sifrant/stopnje/${iso2}`)
      .then(r => setStopnjeZa(prej => ({ ...prej, [iso2]: r.data?.stopnje || [] })))
      .catch(() => setStopnjeZa(prej => ({ ...prej, [iso2]: [] })));
  }, []);

  const nalozi = useCallback(() => {
    if (!companyId) return;
    setNalagam(true);
    Promise.all([
      api.get(`/oss/${companyId}/nastavitve`),
      api.get(`/oss/${companyId}/drzave`),
      api.get('/oss/sifrant/drzave'),
    ]).then(([n, d, s]) => {
      setVkljucen(!!n.data?.oss_vkljucen);
      setOd(n.data?.oss_od ? String(n.data.oss_od).slice(0, 10) : '');
      const vr: Vrstica[] = d.data?.drzave || [];
      setVrstice(vr);
      setSifrant(s.data?.drzave || []);
      vr.forEach(v => naloziStopnje(v.drzava));
    }).catch(e => setNapaka(String(e?.response?.data?.detail || e?.message || e)))
      .finally(() => setNalagam(false));
  }, [companyId, naloziStopnje]);

  useEffect(() => { nalozi(); }, [nalozi]);

  const obvesti = (t: string) => { setSporocilo(t); setTimeout(() => setSporocilo(''), 3000); };

  const shraniNastavitve = () => {
    setNapaka('');
    api.put(`/oss/${companyId}/nastavitve`, { oss_vkljucen: vkljucen, oss_od: od || null })
      .then(() => obvesti('Nastavitve OSS shranjene.'))
      .catch(e => setNapaka(String(e?.response?.data?.detail || e?.message || e)));
  };

  const dodajDrzavo = () => {
    if (!nova) return;
    setNapaka('');
    api.post(`/oss/${companyId}/drzave`, { drzava: nova, aktivna: true, stopnje: [] })
      .then(() => { setNova(''); naloziStopnje(nova); nalozi(); obvesti('Država dodana.'); })
      .catch(e => setNapaka(String(e?.response?.data?.detail || e?.message || e)));
  };

  const shraniDrzavo = (v: Vrstica) => {
    setNapaka('');
    api.put(`/oss/${companyId}/drzave/${v.drzava}`, {
      drzava: v.drzava,
      konto_prihodki_blago: v.konto_prihodki_blago || null,
      konto_prihodki_storitve: v.konto_prihodki_storitve || null,
      aktivna: v.aktivna,
      stopnje: (v.stopnje || []).filter(s => (s.konto_ddv || '').trim()),
    }).then(() => { nalozi(); obvesti(`Država ${v.drzava} shranjena.`); })
      .catch(e => setNapaka(String(e?.response?.data?.detail || e?.message || e)));
  };

  const brisiDrzavo = (iso2: string) => {
    setNapaka('');
    api.delete(`/oss/${companyId}/drzave/${iso2}`)
      .then(() => { nalozi(); obvesti(`Država ${iso2} izbrisana.`); })
      .catch(e => setNapaka(String(e?.response?.data?.detail || e?.message || e)));
  };

  const spremeni = (i: number, sprememba: Partial<Vrstica>) => {
    setVrstice(prej => prej.map((v, idx) => (idx === i ? { ...v, ...sprememba } : v)));
  };

  const spremeniKontoStopnje = (i: number, stopnja: number, konto: string) => {
    setVrstice(prej => prej.map((v, idx) => {
      if (idx !== i) return v;
      const ostale = (v.stopnje || []).filter(s => Math.abs(s.stopnja - stopnja) > 0.001);
      return { ...v, stopnje: konto ? [...ostale, { stopnja, konto_ddv: konto }] : ostale };
    }));
  };

  const kontoZaStopnjo = (v: Vrstica, stopnja: number): string =>
    (v.stopnje || []).find(s => Math.abs(s.stopnja - stopnja) <= 0.01)?.konto_ddv || '';

  const zeDodane = new Set(vrstice.map(v => v.drzava));

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 flex items-center justify-center">
          <Globe className="w-5 h-5" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-white">OSS države</h2>
          <p className="text-xs text-gray-500">
            Unijska ureditev VEM — konti po državi potrošnje in stopnji
          </p>
        </div>
      </div>

      {napaka && (
        <div className="mt-3 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {napaka}
        </div>
      )}
      {sporocilo && (
        <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
          {sporocilo}
        </div>
      )}

      {/* stikalo + datum */}
      <div className="mt-4 flex flex-wrap items-end gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={vkljucen} className="w-4 h-4 accent-emerald-600"
                 onChange={e => setVkljucen(e.target.checked)} />
          Podjetje je vključeno v unijsko ureditev OSS
        </label>
        <div>
          <div className="text-xs text-gray-500 mb-1">Velja od</div>
          <input type="date" value={od} onChange={e => setOd(e.target.value)}
                 className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm" />
        </div>
        <button onClick={shraniNastavitve}
                className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-3 py-1.5">
          Shrani
        </button>
      </div>

      {nalagam ? (
        <div className="mt-4 text-sm text-gray-500">Nalagam …</div>
      ) : (
        <>
          {/* dodajanje drzave */}
          <div className="mt-5 flex items-end gap-2">
            <div>
              <div className="text-xs text-gray-500 mb-1">Dodaj državo</div>
              <select value={nova} onChange={e => setNova(e.target.value)}
                      className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm min-w-[16rem]">
                <option value="">— izberi državo —</option>
                {sifrant.filter(d => !zeDodane.has(d.iso2)).map(d => (
                  <option key={d.iso2} value={d.iso2}>{d.naziv_sl} {d.iso2} {d.num}</option>
                ))}
              </select>
            </div>
            <button onClick={dodajDrzavo} disabled={!nova}
                    className="rounded-lg bg-gray-800 hover:bg-gray-900 disabled:opacity-40 text-white text-sm px-3 py-1.5 flex items-center gap-1">
              <Plus className="w-4 h-4" /> Dodaj
            </button>
          </div>

          {/* seznam drzav */}
          {!vrstice.length && (
            <div className="mt-4 text-sm text-gray-500">Ni nastavljenih držav.</div>
          )}
          {vrstice.map((v, i) => (
            <div key={v.drzava} className="mt-4 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div className="font-medium text-gray-900 dark:text-white">
                  {v.naziv_sl || v.drzava} <span className="text-gray-500 text-sm">{v.drzava} {v.num}</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <input type="checkbox" checked={v.aktivna} className="w-3.5 h-3.5 accent-emerald-600"
                           onChange={e => spremeni(i, { aktivna: e.target.checked })} />
                    aktivna
                  </label>
                  <button onClick={() => brisiDrzavo(v.drzava)} title="Izbriši državo"
                          className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Konto prihodkov — blago</div>
                  <KontoInput companyId={companyId} value={v.konto_prihodki_blago || ''}
                              onChange={k => spremeni(i, { konto_prihodki_blago: k })}
                              placeholder="npr. 762000" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Konto prihodkov — storitve</div>
                  <KontoInput companyId={companyId} value={v.konto_prihodki_storitve || ''}
                              onChange={k => spremeni(i, { konto_prihodki_storitve: k })}
                              placeholder="npr. 760020" />
                </div>
              </div>

              <div className="mt-3">
                <div className="text-xs text-gray-500 mb-1">
                  Konti DDV po stopnjah (stopnje iz šifranta OSS na današnji dan)
                </div>
                {!(stopnjeZa[v.drzava] || []).length ? (
                  <div className="text-xs text-amber-600 dark:text-amber-400">
                    Za {v.drzava} v šifranti OSS ni stopenj na današnji dan.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {(stopnjeZa[v.drzava] || []).map(s => (
                      /* [PATCH oss-temelji v1] kljuc je STOPNJA, ne reza */
                      <div key={String(s.stopnja)}>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                          {String(s.stopnja).replace('.', ',')} % <span className="text-gray-400">({OZNAKA_STOPNJE[s.vrsta_stopnje] || s.vrsta_stopnje})</span>
                        </div>
                        <KontoInput companyId={companyId}
                                    value={kontoZaStopnjo(v, s.stopnja)}
                                    onChange={k => spremeniKontoStopnje(i, s.stopnja, k)}
                                    placeholder="npr. 260093" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-3">
                <button onClick={() => shraniDrzavo(v)}
                        className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-3 py-1.5">
                  Shrani {v.drzava}
                </button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
